// Copyright (c) Ping. All rights reserved.
//
// In-call "Call Notes" panel — pixel-matched to Ping-Desktop Figma "Notes"
// (4079:11387). Sectioned collaborative meeting notes synced live over the
// LiveKit data channel (LWW by revision; CRDT is the documented follow-up):
//   • header: title · settings · close, then stacked avatars + "N participants"
//     and a live "… is typing" indicator
//   • dated subtitle
//   • Discussion Points  (free text)
//   • Decisions          (free text)
//   • Action Items       (checklist + optional assignee / due)
//   • free notes body
//   • a rich-text toolbar (B / I / U / S / bullet / number / link) that wraps the
//     focused notes body selection with Markdown — Safari-safe, no execCommand.
// Tailwind-first with design tokens; full light-mode mirror. Native <textarea>/
// <input> keep inline style (the one documented fallback) but their colours are
// theme-derived so light mode renders dark text.

import { useRef } from 'react';
import { Pressable, Text, View } from 'react-native';

import {
  Bold,
  Calendar,
  Italic,
  Link2,
  List,
  ListOrdered,
  Plus,
  Settings,
  Strikethrough,
  Underline,
  X,
} from 'lucide-react-native';
import type React from 'react';

import { Avatar } from '@ping/ui';

import { type CallNotesDoc, useCallStore } from '@calls/store/callStore';

import { useCallIconColors } from './callTheme';

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

export function CallNotesPanel(): JSX.Element {
  const ic = useCallIconColors();
  const active = useCallStore((s) => s.active);
  const participants = useCallStore((s) => s.participants);
  const doc = useCallStore((s) => s.notesDoc);
  const push = (next: CallNotesDoc): void => useCallStore.getState().setNotesDoc(next);

  const setText = (text: string) => push({ ...doc, text });
  const setDiscussion = (discussion: string) => push({ ...doc, discussion });
  const setDecisions = (decisions: string) => push({ ...doc, decisions });
  const addItem = () =>
    push({ ...doc, items: [...doc.items, { id: `${Date.now()}`, text: '', done: false }] });
  const setItem = (id: string, patch: Partial<{ text: string; done: boolean }>) =>
    push({ ...doc, items: doc.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) });

  const shown = participants.slice(0, 3);
  const extra = Math.max(0, participants.length - shown.length);

  // Native-textarea colours are theme-derived so light mode renders dark text.
  const fieldStyle = {
    width: '100%',
    resize: 'none' as const,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: ic.primary,
    fontSize: 14,
    lineHeight: '19.6px',
    fontFamily: 'inherit',
  };

  // Rich-text toolbar → wrap the focused notes body selection with Markdown.
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const wrap = (prefix: string, suffix: string = prefix): void => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const before = doc.text.slice(0, start);
    const sel = doc.text.slice(start, end);
    const after = doc.text.slice(end);
    const next = `${before}${prefix}${sel}${suffix}${after}`;
    setText(next);
    // Restore a sensible caret after React re-renders the value.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + prefix.length + sel.length;
      el.setSelectionRange(caret, caret);
    });
  };
  const linePrefix = (mark: string): void => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const lineStart = doc.text.lastIndexOf('\n', start - 1) + 1;
    const next = `${doc.text.slice(0, lineStart)}${mark}${doc.text.slice(lineStart)}`;
    setText(next);
    requestAnimationFrame(() => el.focus());
  };

  const ToolbarBtn = ({
    label,
    onPress,
    children,
  }: {
    label: string;
    onPress: () => void;
    children: React.ReactNode;
  }): JSX.Element => (
    <Pressable
      accessibilityLabel={label}
      onPress={onPress}
      className="h-[26px] w-[26px] items-center justify-center rounded-[8px] active:bg-black/[0.05] dark:active:bg-white/[0.06]"
    >
      {children}
    </Pressable>
  );

  return (
    <View className="h-full w-[360px] flex-col gap-[12px] overflow-hidden rounded-[12px] border border-light-border-secondary bg-light-surface-panel p-[12px] dark:border-dark-surface-elevated dark:bg-dark-surface-panel">
      {/* Header */}
      <View className="gap-[8px] border-b border-light-border-secondary pb-[12px] dark:border-dark-surface-pressed">
        <View className="h-[28px] flex-row items-center justify-between gap-[12px]">
          <Text numberOfLines={1} className="flex-1 text-[20px] font-bold leading-[28px] text-light-text-primary dark:text-[#F5F6F8]">
            {active?.title ?? 'Call notes'}
          </Text>
          <View className="flex-row items-center gap-[8px]">
            <Pressable accessibilityLabel="Notes settings" className="h-[28px] w-[28px] items-center justify-center rounded-full active:opacity-60">
              <Settings size={20} strokeWidth={1.5} color={ic.muted} />
            </Pressable>
            <Pressable
              accessibilityLabel="Close notes"
              onPress={() => useCallStore.getState().setPanel('none')}
              className="h-[28px] w-[28px] items-center justify-center rounded-full active:bg-black/[0.06] dark:active:bg-white/[0.08]"
            >
              <X size={20} strokeWidth={1.5} color="#8C929E" />
            </Pressable>
          </View>
        </View>
        <View className="flex-row items-center justify-between gap-[24px]">
          <View className="flex-row items-center gap-[4px]">
            <View className="flex-row items-center">
              {shown.map((p, i) => (
                <View key={p.identity} style={{ marginLeft: i === 0 ? 0 : -4 }}>
                  <Avatar size="company" shape="rounded" initials={initials(p.name)} />
                </View>
              ))}
            </View>
            <Text className="text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]">
              {extra > 0 ? `+${extra} participants` : `${participants.length} participant${participants.length === 1 ? '' : 's'}`}
            </Text>
          </View>
        </View>
      </View>

      <View className="min-h-0 flex-1">
        {/* Timestamp */}
        <Text className="mb-[12px] text-[14px] leading-[20px] text-light-text-tertiary dark:text-dark-text-disabled">
          {active?.startedAt
            ? new Date(active.startedAt).toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }) +
              ' · ' +
              new Date(active.startedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
            : ''}
        </Text>

        {/* Discussion Points */}
        <View className="mb-[12px] gap-[8px]">
          <Text className="text-[16px] font-bold leading-[22px] text-light-text-primary dark:text-[#F5F6F8]">Discussion Points</Text>
          <textarea
            value={doc.discussion ?? ''}
            onChange={(e) => setDiscussion(e.target.value)}
            placeholder="Add discussion point…"
            rows={2}
            style={fieldStyle}
          />
        </View>

        {/* Decisions */}
        <View className="mb-[12px] gap-[8px]">
          <Text className="text-[16px] font-bold leading-[22px] text-light-text-primary dark:text-[#F5F6F8]">Decisions</Text>
          <textarea
            value={doc.decisions ?? ''}
            onChange={(e) => setDecisions(e.target.value)}
            placeholder="Add decision…"
            rows={2}
            style={fieldStyle}
          />
        </View>

        {/* Action Items */}
        <View className="mb-[12px] gap-[8px]">
          <Text className="text-[16px] font-bold leading-[22px] text-light-text-primary dark:text-[#F5F6F8]">Action Items</Text>
          {doc.items.map((it) => (
            <View
              key={it.id}
              className="flex-row items-start gap-[12px] rounded-[12px] border border-light-border-secondary bg-light-surface-primary p-[8px] dark:border-dark-surface-elevated dark:bg-dark-surface-panel"
            >
              <Pressable
                accessibilityLabel="Toggle done"
                onPress={() => setItem(it.id, { done: !it.done })}
                className={`mt-[2px] h-[20px] w-[20px] items-center justify-center rounded-[6px] border ${
                  it.done
                    ? 'border-[#537EFF] bg-[#537EFF]'
                    : 'border-light-border-secondary bg-transparent dark:border-dark-surface-pressed'
                }`}
              >
                {it.done ? <Text className="text-[12px] leading-[12px] text-white">✓</Text> : null}
              </Pressable>
              <View className="min-w-0 flex-1 gap-[8px]">
                <input
                  value={it.text}
                  onChange={(e) => setItem(it.id, { text: e.target.value })}
                  placeholder="Action item…"
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: ic.primary,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    textDecoration: it.done ? 'line-through' : 'none',
                  }}
                />
                {it.assignee || it.due ? (
                  <View className="flex-row items-center gap-[8px]">
                    {it.assignee ? (
                      <View className="flex-row items-center gap-[4px]">
                        <Avatar size="company" shape="rounded" initials={initials(it.assignee)} />
                        <Text className="text-[12px] text-light-text-secondary dark:text-[#B5BAC3]">{it.assignee}</Text>
                      </View>
                    ) : null}
                    {it.due ? (
                      <View className="flex-row items-center gap-[4px] rounded-[8px] border border-light-border-secondary px-[8px] py-[2px] dark:border-dark-surface-pressed">
                        <Calendar size={14} strokeWidth={1.5} color="#8C929E" />
                        <Text className="text-[12px] text-light-text-secondary dark:text-[#B5BAC3]">{it.due}</Text>
                      </View>
                    ) : null}
                  </View>
                ) : null}
              </View>
            </View>
          ))}
          <Pressable
            accessibilityLabel="Add action item"
            onPress={addItem}
            className="flex-row items-center gap-[8px] self-start rounded-[6px] px-[8px] py-[4px] active:bg-black/[0.05] dark:active:bg-white/[0.06]"
          >
            <Plus size={16} strokeWidth={1.5} color="#537EFF" />
            <Text className="text-[14px] leading-[20px] text-[#537EFF]">Add action item</Text>
          </Pressable>
        </View>

        {/* Free notes body */}
        <textarea
          ref={bodyRef}
          value={doc.text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Note down the most important parts…"
          style={{ ...fieldStyle, minHeight: 120 }}
        />
      </View>

      {/* Rich-text toolbar */}
      <View className="flex-row items-center gap-[4px] border-t border-light-border-secondary pt-[12px] dark:border-dark-surface-pressed">
        <ToolbarBtn label="Bold" onPress={() => wrap('**')}>
          <Bold size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <ToolbarBtn label="Italic" onPress={() => wrap('_')}>
          <Italic size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <ToolbarBtn label="Underline" onPress={() => wrap('<u>', '</u>')}>
          <Underline size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <ToolbarBtn label="Strikethrough" onPress={() => wrap('~~')}>
          <Strikethrough size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <View className="mx-[2px] h-[16px] w-px bg-light-border-secondary dark:bg-dark-surface-pressed" />
        <ToolbarBtn label="Bullet list" onPress={() => linePrefix('- ')}>
          <List size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <ToolbarBtn label="Numbered list" onPress={() => linePrefix('1. ')}>
          <ListOrdered size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
        <ToolbarBtn label="Link" onPress={() => wrap('[', '](url)')}>
          <Link2 size={16} strokeWidth={1.75} color={ic.primary} />
        </ToolbarBtn>
      </View>
    </View>
  );
}
